import { Car } from '@upstart/harmony/lib/legos/carTypes';
import {
  Bundle,
  SelectedProduct,
  BundlePrices,
  ProductCategory,
  ConditionFinanceType,
  CoverageNameAndDeductible,
  ProductCoverage,
  CoverageNameAndDeductibleOption,
} from '@upstart/harmony/lib/legos/penTypes';
import {
  FinanceType,
  PenProductSource,
} from '@upstart/harmony/lib/legos/constants';
import { getBestSelectedBundleForProduct } from '@upstart/harmony/lib/pen-utils';
import _, { cloneDeep } from 'lodash';
import { Condition } from '@upstart/harmony/lib/constants';
import { get } from 'lodash/fp';

// TODO: Resolve this interface with ones in harmony
export interface DeskingProduct {
  id: string;
  displayName?: string;
  selected?: boolean;
  term?: number;
  miles?: number;
  monthlyPayment?: number;
  retailPrice?: number;
  finalPrice?: number;
  minRetailPrice?: number;
  maxRetailPrice?: number;
  overridePrice?: number;
  bundlePrices?: BundlePrices;
  source?: string;
  productCategory?: ProductCategory;
  bundleId?: string;
  coverageName?: string;
  deductible?: string;
  inCapCostForLease?: boolean;
  inCapCostForOnePayLease?: boolean;
}

interface SelectedBundle extends Bundle {
  selected?: boolean;
}

export type ProductMap = {
  [productId: string]: DeskingProduct;
};

export type BundleMap = {
  [bundleId: string]: SelectedBundle;
};

export function getSelectedBundles(bundleMap: BundleMap): SelectedBundle[] {
  return Object.values(bundleMap).filter((bundle) => bundle && bundle.selected);
}

export function getSelectedProducts(productMap: ProductMap): DeskingProduct[] {
  return Object.values(productMap).filter(
    (product) => product && product.selected,
  );
}

export function getSelectedProductsWithBundles(
  selectedProducts: DeskingProduct[],
  selectedBundles: Bundle[],
  car: Pick<Car, 'condition'> | undefined,
  financeType: FinanceType | null | undefined,
): SelectedProduct[] {
  return selectedProducts.map((product): SelectedProduct => {
    const bundleIdForProduct = getBestSelectedBundleForProduct(
      product.bundlePrices || {},
      selectedBundles.map((b) => b.id),
    );

    // FIXME: `formatProduct` does not return an object compatible with the
    // `SelectedProduct` interface
    return {
      ...formatProduct(product, car, financeType),
      bundleId: bundleIdForProduct,
    } as unknown as SelectedProduct;
  });
}
/* Below function is a wrapper to parse conditionFinance type object.
It will return the response of Partial<ConditionFinanceType<any>>
Value of condition.financeType will be the value returned by the passed fn
If the passed fn will return undefined, that condition.financeType will be undefined
 */
const parseConditionFinanceType = (
  conditionFinanceTypeObject: Partial<ConditionFinanceType<any>>,
  fn: (
    condition: string,
    financeType: FinanceType,
    conditionFinanceTypeValue: any,
  ) => object | boolean | undefined | any[],
): Partial<ConditionFinanceType<any>> => {
  const newConditionFinanceTypeObject = cloneDeep(conditionFinanceTypeObject);
  return Object.keys(newConditionFinanceTypeObject).reduce(
    (acc, condition) => {
      [FinanceType.PURCHASE, FinanceType.LEASE, FinanceType.CASH].forEach(
        (ft) => {
          const conditionFinanceTypeValue = _.get(
            newConditionFinanceTypeObject,
            `${condition}.${ft}`,
          );
          if (conditionFinanceTypeValue !== undefined) {
            const result = fn(condition, ft, conditionFinanceTypeValue);
            if (result !== undefined) {
              acc = {
                ...acc,
                [condition]: {
                  ...acc[condition as Condition],
                  [ft]: result,
                },
              };
            }
          }
        },
      );
      return acc;
    },
    {} as Partial<ConditionFinanceType<any>>,
  );
};

export const getCoverageNamesAndDeductiblesOptions = (
  availableCoveragesAndDeductibles: ConditionFinanceType<
    ProductCoverage[] | null
  >,
  defaultCoverageNamesAndDeductibles: ConditionFinanceType<CoverageNameAndDeductible | null>,
  productCoverageAndDeducibleOption: Partial<
    ConditionFinanceType<CoverageNameAndDeductibleOption[] | null>
  >,
): Partial<ConditionFinanceType<CoverageNameAndDeductibleOption[] | null>> => {
  if (
    !Object.keys(availableCoveragesAndDeductibles).length &&
    productCoverageAndDeducibleOption
  ) {
    return productCoverageAndDeducibleOption;
  }

  const getDefaultOptions = (
    condition: string,
    ft: FinanceType,
    availableCoverages: ProductCoverage[] | null,
  ) => {
    if (availableCoverages === null) {
      return undefined;
    }
    const coverageAndDeductibleOptions:
      | CoverageNameAndDeductibleOption[]
      | undefined = _.get(
      productCoverageAndDeducibleOption,
      `${condition}.${ft}`,
    );
    return availableCoverages.map((c: ProductCoverage) => {
      const isCoverageAlreadyExist = coverageAndDeductibleOptions?.find(
        (co) => co.coverageName === c.coverageName,
      );
      if (isCoverageAlreadyExist) {
        return isCoverageAlreadyExist;
      }
      const defaultCoverage: CoverageNameAndDeductible = _.get(
        defaultCoverageNamesAndDeductibles,
        `${condition}.${ft}`,
      );
      const isDefault = c.coverageName === defaultCoverage?.coverageName;

      let deductible = '';
      if (isDefault) {
        deductible = defaultCoverage.deductible || '';
      } else if (
        c?.allDeductiblesForCoverage &&
        Object.keys(c?.allDeductiblesForCoverage || {}).length === 1
      ) {
        deductible = Object.keys(c?.allDeductiblesForCoverage)[0];
      }

      return {
        isDefault,
        coverageName: c.coverageName,
        deductible,
        displayName: '',
        instoreEnabled: true,
        minAge: null,
        maxAge: null,
        maxMileage: null,
        minMileage: null,
        allDeductiblesForCoverage: c.allDeductiblesForCoverage,
      };
    });
  };

  return parseConditionFinanceType(
    availableCoveragesAndDeductibles,
    getDefaultOptions,
  ) as ConditionFinanceType<CoverageNameAndDeductibleOption[] | null>;
};

export const getDefaultCoverageNamesAndDeductibles = (
  coverageNameAndDeductibleOptions: Partial<
    ConditionFinanceType<CoverageNameAndDeductibleOption[]>
  >,
): Partial<ConditionFinanceType<CoverageNameAndDeductible | null>> => {
  if (!coverageNameAndDeductibleOptions) {
    return {
      New: {
        cash: {},
        lease: {},
        purchase: {},
      },
      Used: {
        cash: {},
        purchase: {},
      },
    };
  }
  const getDefaultCoveragesAndDeductibles = (
    condition: string,
    ft: FinanceType,
    options: CoverageNameAndDeductibleOption[],
  ) => {
    const defaultCoverage = options.find((o) => o.isDefault);
    if (defaultCoverage) {
      const {
        displayName,
        coverageName,
        allDeductiblesForCoverage,
        deductible,
      } = defaultCoverage;
      return {
        displayName,
        coverageName,
        deductible,
        allDeductiblesForCoverage,
      };
    }
    return undefined;
  };

  return parseConditionFinanceType(
    coverageNameAndDeductibleOptions,
    getDefaultCoveragesAndDeductibles,
  ) as ConditionFinanceType<CoverageNameAndDeductible | null>;
};

export const formatProduct = (
  product: DeskingProduct,
  car: Pick<Car, 'condition'> | undefined,
  financeType: FinanceType | undefined | null,
) => {
  const formattedProduct = {
    productId: product.id,
    displayName: product.displayName,
    termMiles: get('miles')(product) || get('termMiles')(product) || 0,
    termMonths: get('term')(product) || get('termMonths')(product) || 0,
    taxed: !!get('taxed')(product),
    retailPrice:
      get('deductible.retailPrice')(product) ||
      get('retailPrice')(product) ||
      0,
    minRetailPrice: get('deductible.minRetailPrice')(product),
    maxRetailPrice: get('deductible.maxRetailPrice')(product),
    monthlyPayment:
      get('deductible.monthlyPayment')(product) ||
      get('monthlyPayment')(product) ||
      0,
    coverageName: product.coverageName || '',
    deductible: product.deductible || '',
    inCapCostForLease: product.inCapCostForLease,
    inCapCostForOnePayLease: product.inCapCostForOnePayLease,
  };
  // CoverageName and Deductible are only available on Provider (PEN) products
  if (product.source === PenProductSource.PROVIDER) {
    formattedProduct.coverageName =
      product.coverageName ||
      get(
        `coverageNamesAndDeductibles[${car?.condition}][${financeType}].coverageName`,
      )(product);
    formattedProduct.deductible = get(
      `coverageNamesAndDeductibles[${car?.condition}][${financeType}].deductible`,
    )(product);
  }

  return formattedProduct;
};
